import React, { useMemo, useState } from 'react';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import '../csscomponent/Contact.css';
import Stamp from './Stamp';
import { getCmsSettings } from '../lib/sqlAuth';
import { subscribeCmsUpdates } from '../lib/cmsSync';

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const isGmail = (value) => /^[^\s@]+@gmail\.com$/i.test(value.trim());

const normalizeReceiver = (value) => {
  const trimmed = (value || '').trim();
  if (isValidEmail(trimmed)) return trimmed;
  if (/^[^\s@]+@[^\s@]+$/.test(trimmed)) return `${trimmed}.com`;
  return '';
};

function Contact() {
  const contactLottieSrc = `${process.env.PUBLIC_URL || ''}/contactus.lottie`;
  const maskedPhone = '81*******77';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState({
    eyebrow: 'Contact Us',
    title: 'Talk to our team',
    description: 'Share your requirement and we will get back with the right solution, timeline, and next steps.',
    phone: '+91 8177966877',
    availability: 'Mon - Sat, 10:00 AM to 7:00 PM',
    priority: 'Business & Enterprise package requests',
  });
  const callPhone = content.phone.replace(/\D/g, '');

  // Keep mailbox hidden from UI while still allowing direct form-to-email submission.
  const mailbox = useMemo(() => {
    const fromEnv = normalizeReceiver(process.env.REACT_APP_CONTACT_RECEIVER);
    if (fromEnv) return fromEnv;
    return normalizeReceiver(atob('aW5mb0BzZXJ2aWNlc2FuZGRldmVsb3Blci5pbg=='));
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await getCmsSettings({ keys: ['contact_info'] });
        if (!mounted) return;
        const next = res?.settings?.contact_info || {};
        setContent((prev) => ({ ...prev, ...next }));
      } catch (_) {}
    };
    load();
    const unsubscribe = subscribeCmsUpdates((payload) => {
      if (!payload?.scope || ['site', 'contact'].includes(payload.scope)) {
        load();
      }
    });
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setStatus({ type: '', text: '' });

    if (!mailbox) {
      setStatus({
        type: 'error',
        text: 'Contact mailbox is not configured correctly. Please update receiver email settings.',
      });
      setLoading(false);
      return;
    }

    if (!isGmail(formData.email)) {
      setStatus({ type: 'error', text: 'Please enter a valid Gmail ID (example: you@gmail.com).' });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`https://formsubmit.co/ajax/${encodeURIComponent(mailbox)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          _replyto: formData.email,
          subject: formData.subject,
          message: `Name: ${formData.name}\nPhone: ${formData.phone}\n\n${formData.message}`,
          _subject: `Website Contact: ${formData.subject}`,
          _template: 'table',
          _captcha: 'false',
        }),
      });

      const data = await response.json().catch(() => ({}));
      const apiMessage = typeof data.message === 'string' ? data.message : '';
      const apiSuccess = data.success === true || data.success === 'true';
      const delivered = response.ok && (apiSuccess || /sent|success/i.test(apiMessage));

      if (delivered) {
        setStatus({ type: 'success', text: 'Message sent successfully. We will contact you soon.' });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setStatus({
          type: 'error',
          text: apiMessage || 'Unable to send right now. Please try again in a moment.',
        });
      }
    } catch (error) {
      setStatus({ type: 'error', text: 'Network issue while sending. Please retry.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="contact-page">
      <motion.div
        className="contact-hero"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="contact-hero-copy">
          <p>{content.eyebrow}</p>
          <h1>{content.title}</h1>
          <span>{content.description}</span>
        </div>
        <Stamp className="contact-stamp" />
      </motion.div>

      <div className="contact-layout">
        <motion.form
          className="contact-form"
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.1 }}
        >
          <h3>Send Message</h3>
          <label htmlFor="name">Your Name</label>
          <input
            id="name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your name"
            required
          />

          <label htmlFor="email">Your Gmail ID</label>
          <input
            id="email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@gmail.com"
            required
          />

          <label htmlFor="phone">Phone Number</label>
          <input
            id="phone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Enter your phone number"
            required
          />

          <label htmlFor="subject">Subject</label>
          <input
            id="subject"
            type="text"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder="Project subject"
            required
          />

          <label htmlFor="message">Message</label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Write your message"
            rows="6"
            required
          />

          <button type="submit" disabled={loading}>
            {loading ? 'Sending...' : 'Send Message'}
          </button>

          {status.text ? <p className={`form-status ${status.type}`}>{status.text}</p> : null}
        </motion.form>

        <motion.aside
          className="contact-info"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.2 }}
        >
          <h3>Support Details</h3>
          <div className="contact-animation-wrap">
            <DotLottieReact src={contactLottieSrc} autoplay loop className="contact-lottie" />
          </div>
          <p className="contact-info-intro">
            We respond to product, service, and package inquiries with practical recommendations and transparent
            estimates.
          </p>
          <div>
            <span>Phone</span>
            <div className="contact-phone-row">
              <strong>{maskedPhone}</strong>
              <a className="contact-call-btn" href={`tel:${callPhone}`}>
                Call
              </a>
            </div>
          </div>
          <div>
            <span>Availability</span>
            <strong>{content.availability}</strong>
          </div>
          <div>
            <span>Priority</span>
            <strong>{content.priority}</strong>
          </div>
        </motion.aside>
      </div>
    </section>
  );
}

export default Contact;
